import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Users, MapPin, Heart, UserPlus } from "lucide-react";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { Skeleton } from "@/components/ui/skeleton";

export function NotificationSettings() {
  const { preferences, isLoading, isUpdating, updatePreference } = useNotificationPreferences();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <Skeleton className="h-6 w-32" />
          </CardTitle>
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  const notificationOptions = [
    {
      key: 'email_new_followers' as const,
      icon: UserPlus,
      label: 'New Followers',
      description: 'Get notified when someone follows you',
    },
    {
      key: 'email_followed_user_runs' as const,
      icon: Users,
      label: 'Following Activity',
      description: 'Get notified when users you follow post new adventures',
    },
    {
      key: 'email_local_runs' as const,
      icon: MapPin,
      label: 'Nearby Adventures',
      description: 'Get notified when someone posts an adventure in your area',
    },
    {
      key: 'email_run_likes' as const,
      icon: Heart,
      label: 'Likes',
      description: 'Get notified when someone likes your adventure',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Email Notifications
        </CardTitle>
        <CardDescription>Choose which emails you'd like to receive</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {notificationOptions.map((option) => {
          const Icon = option.icon;
          return (
            <div key={option.key} className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-2 rounded-lg bg-muted">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor={option.key} className="font-medium cursor-pointer">
                    {option.label}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </div>
              <Switch
                id={option.key}
                checked={preferences[option.key]}
                onCheckedChange={(checked) => updatePreference(option.key, checked)}
                disabled={isUpdating}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
